/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import './Assessment.css';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FB12-FB28 Feedback page (Section 4.5)
 * Shown after "Answer feedback questions" button on the report page.
 * Every rating and Yes/No question must be answered.
 * Open text questions are optional.
 * "Completed" greyed out until all required answered.
 */

const FEEDBACK_QUESTIONS = [
  { id: 'FB12', type: 'rating', question: 'My suggested pathway felt accurate to me.' },
  { id: 'FB13', type: 'rating', question: 'The reasoning behind it made sense to me.' },
  { id: 'FB14', type: 'rating', question: 'It read like a recommendation, not a final decision made for me.' },
  { id: 'FB15', type: 'rating', question: 'I was recommended two possible pathways, and the choice was mine. That felt like a genuine choice.' },
  { id: 'FB16', type: 'rating', question: 'This result felt respectful, not like I had failed something.' },
  { id: 'FB17', type: 'rating', question: 'The reasoning I was shown felt backed up by enough of my own answers.' },
  { id: 'FB18', type: 'text', question: 'Was there anything about your result or suggested pathway that surprised you, or did not sit right?', optional: true },
  { id: 'FB19', type: 'rating', question: 'On a scale of 1 to 5, how much did you want to continue with your suggested pathway?' },
  { id: 'FB20', type: 'text', question: 'If you did not want to continue with your suggested pathway, why not?', optional: true },
  { id: 'FB21', type: 'rating', question: 'On a scale of 1 to 5, how much did you want to take one of the other pathways instead?' },
  { id: 'FB22', type: 'text', question: 'If you wanted a different pathway, which one, and why?', optional: true },
  { id: 'FB23', type: 'yesno', question: 'Would you want to continue and work through a pathway?' },
  { id: 'FB24', type: 'rating', question: 'Overall, the platform felt professional and trustworthy.' },
  { id: 'FB25', type: 'rating', question: 'Overall, it was easy to use.' },
  { id: 'FB26', type: 'rating', question: 'The design of the site felt well put together.' },
  { id: 'FB27', type: 'rating', question: 'The wording throughout felt clear and well written.' },
  { id: 'FB28', type: 'text', question: 'Anything else you want to tell us?', optional: true },
];

const REQUIRED_IDS = FEEDBACK_QUESTIONS.filter(q => !q.optional).map(q => q.id);
const RATING_LABELS = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree'];

export default function FeedbackPage() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const allRequiredAnswered = REQUIRED_IDS.every(id => responses[id] !== undefined && responses[id] !== '');
  const canSubmit = allRequiredAnswered && !submitting;

  const setResponse = (id, value) => {
    setResponses(prev => ({ ...prev, [id]: value }));
  };

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      // Save all responses
      const saves = Object.entries(responses).map(([qid, val]) => {
        const q = FEEDBACK_QUESTIONS.find(fq => fq.id === qid);
        return axios.post(`${API}/api/feedback-module/${attemptId}`, {
          question_id: qid,
          checkpoint: 'report_feedback',
          response_type: q?.type === 'text' ? 'text' : q?.type === 'yesno' ? 'yesno' : 'rating',
          rating_value: q?.type === 'rating' ? val : undefined,
          text_value: q?.type === 'text' ? val : q?.type === 'yesno' ? val : undefined,
        });
      });
      await Promise.all(saves);

      // Mark feedback as completed on the attempt
      await axios.post(`${API}/api/free-assessment/${attemptId}/mark-feedback-completed`);

      setDone(true);
      window.scrollTo(0, 0);
      toast.success('Thank you for your feedback.');
      // Return to report after 2 seconds
      setTimeout(() => navigate(`/assessment/results/${attemptId}`), 2000);
    } catch (err) {
      toast.error('Could not save feedback. Please try again.');
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="fb-page" data-testid="feedback-done">
        <div className="fb-inner">
          <h2 className="fb-done-heading">Completed. Thank you for your feedback.</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="fb-page" data-testid="feedback-page">
      <div className="fb-inner">
        <h1 className="fb-heading">Your feedback on your report</h1>

        {FEEDBACK_QUESTIONS.map(q => (
          <div key={q.id} className="fb-question" data-testid={`fb-${q.id}`}>
            <p className="fb-question-text">
              {q.question}
              {q.optional && <span className="fb-optional"> (optional)</span>}
            </p>

            {q.type === 'rating' && (
              <div className="fb-rating-row">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    className={`fb-rating-btn ${responses[q.id] === n ? 'active' : ''}`}
                    onClick={() => setResponse(q.id, n)}
                    data-testid={`fb-${q.id}-${n}`}
                    title={RATING_LABELS[n - 1]}
                  >
                    {n}
                  </button>
                ))}
                <span className="fb-rating-labels">
                  <span>Strongly disagree</span>
                  <span>Strongly agree</span>
                </span>
              </div>
            )}

            {q.type === 'yesno' && (
              <div className="fb-yesno-row">
                <button
                  className={`fb-yesno-btn ${responses[q.id] === 'Yes' ? 'active' : ''}`}
                  onClick={() => setResponse(q.id, 'Yes')}
                  data-testid={`fb-${q.id}-yes`}
                >Yes</button>
                <button
                  className={`fb-yesno-btn ${responses[q.id] === 'No' ? 'active' : ''}`}
                  onClick={() => setResponse(q.id, 'No')}
                  data-testid={`fb-${q.id}-no`}
                >No</button>
              </div>
            )}

            {q.type === 'text' && (
              <textarea
                className="fb-textarea"
                value={responses[q.id] || ''}
                onChange={e => setResponse(q.id, e.target.value)}
                placeholder="Your thoughts..."
                data-testid={`fb-${q.id}-text`}
              />
            )}
          </div>
        ))}

        <button
          className={`rpt-btn-primary rpt-btn-full ${!canSubmit ? 'disabled' : ''}`}
          onClick={handleComplete}
          disabled={!canSubmit}
          data-testid="fb-complete-btn"
        >
          {submitting ? 'Saving...' : 'Completed'}
        </button>
      </div>
    </div>
  );
}
